import { FinancialTimeSeriesPoint } from '@/lib/api/financial/financial-dashboard-api'
import { useContainerWidth } from '@/lib/hooks/use-container-width'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    Area,
    AreaChart,
    CartesianGrid,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'

interface RevenueCostChartProps {
    data: FinancialTimeSeriesPoint[]
    granularity: 'hour' | 'day' | 'week' | 'month'
}

const CHART_HEIGHT = 300

const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(cents / 100)

const dateFormatFor = (granularity: string) => {
    switch (granularity) {
        case 'hour':
            return 'HH:mm'
        case 'month':
            return 'MMM/yy'
        case 'week':
            return "'Sem.' w"
        default:
            return 'dd/MM'
    }
}

/**
 * As quatro séries, na ordem em que se lê o resultado: o que entrou, o que a venda
 * custou, o que a operação gastou e o que sobrou.
 */
const SERIES: Array<{
    key: 'revenue' | 'cost' | 'expense' | 'profit'
    label: string
    color: string
    gradientId: string
}> = [
    { key: 'revenue', label: 'Receita', color: '#22c55e', gradientId: 'fin-rev' },
    { key: 'cost', label: 'Custo', color: '#f97316', gradientId: 'fin-cost' },
    { key: 'expense', label: 'Despesa', color: '#ef4444', gradientId: 'fin-exp' },
    { key: 'profit', label: 'Lucro', color: '#3b82f6', gradientId: 'fin-profit' },
]

const SERIES_LABELS: Record<string, string> = Object.fromEntries(
    SERIES.map(s => [s.key, s.label]),
)

/**
 * Legenda em HTML, e não o `<Legend>` do Recharts: o CSS global
 * (`.recharts-legend-wrapper { position: relative !important }`) colapsa o wrapper do
 * gráfico para largura zero e o SVG some — sem erro nenhum no console. Ver
 * `useContainerWidth`, que explica o mesmo problema no `<ResponsiveContainer>`.
 */
function ChartLegend() {
    return (
        <div className="mb-2 flex flex-wrap items-center gap-4 text-xs text-base-content/70">
            {SERIES.map(series => (
                <span key={series.key} className="flex items-center gap-1.5">
                    <span
                        className="inline-block h-2.5 w-2.5 rounded-sm"
                        style={{ backgroundColor: series.color }}
                    />
                    {series.label}
                </span>
            ))}
        </div>
    )
}

/**
 * Receita, custo, despesa e lucro realizados no período.
 *
 * **Custo e despesa são coisas diferentes** e ficam em linhas separadas de propósito:
 * custo é o que a receita daquele período consumiu (repasse congelado na venda mais
 * insumos); despesa é o gasto da operação que não pertence a venda nenhuma (aluguel,
 * energia, material, folha). Repasse não aparece na linha de despesa — ele já está no
 * custo, e somá-lo nas duas contaria o mesmo dinheiro duas vezes.
 */
export default function RevenueCostChart({
    data,
    granularity,
}: RevenueCostChartProps) {
    const { ref, width } = useContainerWidth()
    const fmt = dateFormatFor(granularity)
    const chartData = data.map(p => ({
        label: format(new Date(p.date), fmt, { locale: ptBR }),
        revenue: p.revenue / 100,
        cost: p.cost / 100,
        expense: p.expense / 100,
        profit: p.profit / 100,
    }))

    const totals = data.reduce(
        (acc, p) => ({
            revenue: acc.revenue + p.revenue,
            cost: acc.cost + p.cost,
            expense: acc.expense + p.expense,
        }),
        { revenue: 0, cost: 0, expense: 0 },
    )
    const totalProfit = totals.revenue - totals.cost - totals.expense

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-base-content">
                        Receita, Custo e Lucro
                    </h3>
                    <p className="text-sm text-base-content/60">
                        Valores realizados no período
                    </p>
                </div>
                <div className="flex flex-wrap gap-4 text-right">
                    <div>
                        <p className="text-xs text-base-content/60">Receita</p>
                        <p className="text-base font-semibold text-success">
                            {formatCurrency(totals.revenue)}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-base-content/60">Custo</p>
                        <p className="text-base font-semibold text-warning">
                            {formatCurrency(totals.cost)}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-base-content/60">Despesa</p>
                        <p className="text-base font-semibold text-error">
                            {formatCurrency(totals.expense)}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-base-content/60">Lucro</p>
                        <p
                            className={`text-base font-semibold ${
                                totalProfit >= 0 ? 'text-secondary' : 'text-error'
                            }`}
                        >
                            {formatCurrency(totalProfit)}
                        </p>
                    </div>
                </div>
            </div>

            {chartData.length === 0 ? (
                <div className="flex items-center justify-center h-64 text-base-content/50">
                    Sem movimentação no período
                </div>
            ) : (
                <>
                    <ChartLegend />
                    <div
                        ref={ref}
                        className="w-full"
                        style={{ height: CHART_HEIGHT, minHeight: CHART_HEIGHT }}
                    >
                        {width > 0 && (
                            <AreaChart
                                data={chartData}
                                width={width}
                                height={CHART_HEIGHT}
                                margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                            >
                                <defs>
                                    {SERIES.map(series => (
                                        <linearGradient
                                            key={series.gradientId}
                                            id={series.gradientId}
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="5%"
                                                stopColor={series.color}
                                                stopOpacity={0.4}
                                            />
                                            <stop
                                                offset="95%"
                                                stopColor={series.color}
                                                stopOpacity={0}
                                            />
                                        </linearGradient>
                                    ))}
                                </defs>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    className="opacity-30"
                                />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 12 }}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <YAxis
                                    tick={{ fontSize: 12 }}
                                    tickLine={false}
                                    axisLine={false}
                                    width={72}
                                    tickFormatter={value =>
                                        new Intl.NumberFormat('pt-BR', {
                                            notation: 'compact',
                                            maximumFractionDigits: 1,
                                        }).format(Number(value) || 0)
                                    }
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'oklch(var(--b1))',
                                        border: '1px solid oklch(var(--b3))',
                                        borderRadius: 8,
                                    }}
                                    formatter={(value, name) => [
                                        new Intl.NumberFormat('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                        }).format(Number(value) || 0),
                                        SERIES_LABELS[String(name)] ?? String(name),
                                    ]}
                                />
                                {SERIES.map(series => (
                                    <Area
                                        key={series.key}
                                        type="monotone"
                                        dataKey={series.key}
                                        stroke={series.color}
                                        fill={`url(#${series.gradientId})`}
                                        strokeWidth={2}
                                    />
                                ))}
                            </AreaChart>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}
